@if (projectId) {
  <div class="project-search" [class.expanded]="expanded" cdkOverlayOrigin #searchOrigin="cdkOverlayOrigin">
    <!-- At phone width the field hides behind this button (Issue #2750). -->
    <button
      mat-icon-button
      type="button"
      class="search-toggle"
      aria-label="Search this project"
      data-testid="project-search-toggle"
      (click)="expand()"
    >
      <mat-icon aria-hidden="true">search</mat-icon>
    </button>

    <div class="search-field" role="search" #searchField>
      <mat-icon class="search-icon" aria-hidden="true">search</mat-icon>
      <input
        #searchInput
        type="search"
        class="search-input"
        placeholder="Search this project"
        aria-label="Search this project"
        role="combobox"
        aria-autocomplete="list"
        autocomplete="off"
        spellcheck="false"
        enterkeyhint="search"
        data-testid="project-search-input"
        [value]="query"
        [attr.aria-expanded]="panelOpen ? 'true' : 'false'"
        [attr.aria-controls]="panelOpen ? listboxId : null"
        [attr.aria-activedescendant]="activeRowId"
        (input)="onInput($event)"
        (keydown)="onInputKeydown($event)"
        (focus)="onFocus()"
      />
      @if (query) {
        <button mat-icon-button type="button" class="clear-button" aria-label="Clear search" (click)="clear()">
          <mat-icon aria-hidden="true">close</mat-icon>
        </button>
      } @else {
        <kbd class="shortcut" aria-hidden="true">/</kbd>
      }
    </div>
  </div>

  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="searchOrigin"
    [cdkConnectedOverlayOpen]="panelOpen"
    [cdkConnectedOverlayPositions]="panelPositions"
    [cdkConnectedOverlayPush]="true"
    [cdkConnectedOverlayViewportMargin]="8"
    [cdkConnectedOverlayWidth]="panelWidth"
    cdkConnectedOverlayPanelClass="project-search-overlay"
    (overlayOutsideClick)="onOutsideClick($event)"
    (detach)="onPanelDetached()"
  >
    <div class="search-panel" data-testid="project-search-panel">
      <div class="panel-header">
        <span class="panel-scope">This project</span>
        @if (status === 'done' && result) {
          <span class="panel-count" role="status" aria-live="polite">{{ countText }}</span>
        }
      </div>

      @if (status === 'loading') {
        <mat-progress-bar mode="indeterminate" aria-label="Searching"></mat-progress-bar>
        @if (waitMessage) {
          <p class="panel-message panel-wait" role="status" data-testid="project-search-wait">
            <mat-icon aria-hidden="true">hourglass_top</mat-icon>
            <span>{{ waitMessage }}</span>
          </p>
        }
      }

      @if (status === 'error') {
        <p class="panel-message panel-error" role="alert" data-testid="project-search-error">
          <mat-icon aria-hidden="true">error</mat-icon>
          <span>{{ errorMessage }}</span>
        </p>
      }

      @if (status === 'done' && result?.notice) {
        <p
          class="panel-message panel-notice"
          data-testid="project-search-notice"
          [class.panel-warning]="result?.noticeTone === 'warning'"
        >
          <mat-icon aria-hidden="true">{{ result?.noticeTone === 'warning' ? 'warning' : 'info' }}</mat-icon>
          <span>{{ result?.notice }}</span>
        </p>
      }

      @if (status === 'done' && result && rows.length === 0) {
        <p class="panel-message panel-empty">
          Nothing found for “{{ result.query }}”.
          @if (result.kind === 'exact') {
            Exact search finds the words only as they are written on the sheets.
          }
        </p>
      }

      @if (rows.length > 0) {
        <div class="results" role="listbox" [id]="listboxId" aria-label="Search results">
          @if (sheetRows.length > 0) {
            <div class="result-group" role="group" aria-labelledby="project-search-sheets">
              <div class="section-heading" id="project-search-sheets">
                Sheets
                @if (sheetTotal > sheetRows.length) {
                  <span class="section-count">{{ sheetRows.length }} of {{ sheetTotal }}</span>
                }
              </div>
              @for (row of sheetRows; track row.id) {
                <ng-container *ngTemplateOutlet="resultRow; context: { $implicit: row }"></ng-container>
              }
            </div>
          }
          @if (fileRows.length > 0) {
            <div class="result-group" role="group" aria-labelledby="project-search-files">
              <div class="section-heading" id="project-search-files">Files</div>
              @for (row of fileRows; track row.id) {
                <ng-container *ngTemplateOutlet="resultRow; context: { $implicit: row }"></ng-container>
              }
            </div>
          }
          @if (groups.length > 0) {
            <div class="section-heading">{{ pageResultsHeading }}</div>
            @for (group of groups; track group.key) {
              <div class="result-group" role="group" [attr.aria-labelledby]="group.headingId">
                <div class="group-heading" [id]="group.headingId">
                  <ng-container *ngTemplateOutlet="sheetIcon; context: { $implicit: group.pageNumber }"></ng-container>
                  <span class="group-title">{{ group.heading }}</span>
                  @if (group.fileName) {
                    <ng-container *ngTemplateOutlet="fileLine; context: { $implicit: group.fileName }"></ng-container>
                  }
                </div>
                @for (row of group.rows; track row.id) {
                  <ng-container *ngTemplateOutlet="resultRow; context: { $implicit: row }"></ng-container>
                }
              </div>
            }
          }
        </div>
      }

      @if (status === 'done' && result) {
        <div class="panel-footer">
          @if (result.summary) {
            <span class="panel-summary" [attr.title]="result.summary">{{ result.summary }}</span>
          }
          <span class="panel-keys" aria-hidden="true"
            ><kbd>↑</kbd><kbd>↓</kbd> move · <kbd>Enter</kbd> open · <kbd>Esc</kbd> close</span
          >
        </div>
      }
    </div>
  </ng-template>
}

<!-- One result row, the same for every kind; the icon says what the row stands for. -->
<ng-template #resultRow let-row>
  <div
    class="result"
    role="option"
    [id]="row.id"
    [class.active]="row.index === activeIndex"
    [class.opening]="opening === row"
    [attr.aria-selected]="row.index === activeIndex ? 'true' : 'false'"
    [attr.data-kind]="row.kind"
    tabindex="-1"
    data-testid="project-search-result"
    (mousedown)="$event.preventDefault()"
    (mouseenter)="activate(row)"
    (click)="open(row)"
    (keydown.enter)="open(row)"
  >
    @if (row.kind === 'sheet') {
      <ng-container *ngTemplateOutlet="sheetIcon; context: { $implicit: row.pageNumber }"></ng-container>
    } @else {
      <mat-icon class="result-icon" aria-hidden="true" [fontSet]="row.icon.fontSet">{{ row.icon.name }}</mat-icon>
    }
    <span class="result-main">
      <!-- No white space between the runs: it would show between a word and its mark. -->
      <span class="result-text" data-testid="project-search-result-text">
        @for (segment of row.segments; track $index) {
          <span class="segment" [class.mark]="segment.mark">{{ segment.text }}</span>
        }
      </span>
      @if (row.fileName) {
        <ng-container *ngTemplateOutlet="fileLine; context: { $implicit: row.fileName }"></ng-container>
      } @else if (row.detail || row.matchLabel || row.diagnostic) {
        <!-- Under the snippet, so the snippet gets the row's full width (Issue #3063). -->
        <span class="result-meta">
          @if (row.detail) {
            <span class="result-detail" data-testid="project-search-result-detail">{{ row.detail }}</span>
          }
          @if (row.matchLabel) {
            <span class="match-label" [class.related]="row.related" [attr.title]="row.matchTooltip">{{
              row.matchLabel
            }}</span>
          }
          @if (row.diagnostic) {
            <span class="result-diagnostic" data-testid="project-search-result-diagnostic">{{ row.diagnostic }}</span>
          }
        </span>
      }
    </span>
  </div>
</ng-template>

<!--
  A sheet's icon, the same as in the project contents tree: the outlined sheet symbol with the page
  number in a circle (Issue #3042). A screen reader hears "Page N" instead of the circle.
-->
<ng-template #sheetIcon let-page>
  <span class="sheet-icon" data-testid="project-search-sheet-icon">
    <mat-icon fontSet="material-symbols-outlined" aria-hidden="true">{{ icons.sheet.name }}</mat-icon>
    <span class="page-badge" aria-hidden="true">{{ page }}</span>
    <span class="visually-hidden">Page {{ page }}</span>
  </span>
</ng-template>

<!-- The file a sheet belongs to, after a PDF icon so it reads as the file and not as a caption. -->
<ng-template #fileLine let-name>
  <span class="file-name" data-testid="project-search-file-name">
    <mat-icon fontSet="material-symbols-outlined" aria-hidden="true">{{ icons.file.name }}</mat-icon>
    <span class="file-name-text">{{ name }}</span>
  </span>
</ng-template>
